import { useState, useEffect, useRef, useCallback } from 'react';

export function useSpeechRecognition({ onSpeechEnd, pauseDuration = 1400 } = {}) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const currentTranscriptRef = useRef('');
  const onSpeechEndRef = useRef(onSpeechEnd);

  // Keep callback ref updated
  useEffect(() => {
    onSpeechEndRef.current = onSpeechEnd;
  }, [onSpeechEnd]);

  const SpeechRecognition = typeof window !== 'undefined' 
    ? (window.SpeechRecognition || window.webkitSpeechRecognition) 
    : null;

  const hasSupport = Boolean(SpeechRecognition);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    setIsPaused(false);
  }, []);

  const stopListening = useCallback(() => {
    clearSilenceTimer();
    try {
      recognitionRef.current?.stop();
    } catch (e) {}
    setIsListening(false);
  }, [clearSilenceTimer]);

  // Client-side ERP speech normalizer
  const normalizeSpeechText = (raw) => {
    if (!raw) return '';
    return raw
      .replace(/\blicence\b/gi, 'license')
      .replace(/\blicences\b/gi, 'licenses')
      .replace(/\bavailibility\b/gi, 'availability')
      .replace(/\bavailablity\b/gi, 'availability')
      .replace(/\bavialable\b/gi, 'available')
      .replace(/\bavaiable\b/gi, 'available')
      .replace(/\bsubcription\b/gi, 'subscription')
      .replace(/\b(create|draft|make|raise|new)\s+p\s*\.?\s*o\s*\.?\b/gi, 'create po')
      .replace(/\b(create|draft|make|raise|new)\s+p\s*\.?\s*r\s*\.?\b/gi, 'create pr')
      .replace(/\bp\s*\.?\s*o\s*\.?\s+orders?\b/gi, 'purchase orders')
      .replace(/\bp\s*\.?\s*o\s*\.?\b/gi, 'po')
      .replace(/\bp\s*\.?\s*r\s*\.?\b/gi, 'pr')
      .replace(/\bg\s*\.?\s*r\s*\.?\s*n\s*\.?\b/gi, 'grn')
      .replace(/\bg\s*\.?\s*l\s*\.?\b/gi, 'gl')
      .replace(/\btrail balance\b/gi, 'trial balance')
      .replace(/\btry balance\b/gi, 'trial balance')
      .trim();
  };

  useEffect(() => {
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    // Set to British English / English
    recognition.lang = 'en-GB';

    recognition.onresult = (event) => {
      let current = '';
      for (let i = 0; i < event.results.length; i++) {
        current += event.results[i][0].transcript;
      }
      const trimmed = normalizeSpeechText(current.trim());
      currentTranscriptRef.current = trimmed;
      setTranscript(trimmed);

      // Intelligent Pause Detection:
      // If the user has spoken words and then pauses for pauseDuration (1.4s),
      // understand the pause, stop listening, and automatically trigger the action!
      if (trimmed.length > 0) {
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
        }
        setIsPaused(false);

        silenceTimerRef.current = setTimeout(() => {
          setIsPaused(true);
          const finalSpoken = currentTranscriptRef.current;
          if (finalSpoken) {
            // User finished speaking and paused!
            try {
              recognition.stop();
            } catch (e) {}
            setIsListening(false);
            if (typeof onSpeechEndRef.current === 'function') {
              onSpeechEndRef.current(finalSpoken);
            }
          }
        }, pauseDuration);
      }
    };

    recognition.onerror = (event) => {
      console.warn('Speech Recognition Event:', event.error);
      clearSilenceTimer();
      if (event.error === 'not-allowed') {
        setError('Microphone access was denied. Please allow microphone permissions.');
      } else if (event.error === 'no-speech') {
        // Ignored, user is quiet
      } else {
        setError(event.error);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      clearSilenceTimer();
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      clearSilenceTimer();
      try {
        recognition.stop();
      } catch (e) {}
    };
  }, [SpeechRecognition, pauseDuration, clearSilenceTimer]);

  const startListening = useCallback(() => {
    if (!hasSupport) {
      setError('Speech Recognition is not supported in this browser.');
      return;
    }
    setError(null);
    setTranscript('');
    currentTranscriptRef.current = '';
    clearSilenceTimer();

    try {
      recognitionRef.current?.start();
      setIsListening(true);
    } catch (e) {
      console.warn('Speech recognition already active or error starting:', e);
    }
  }, [hasSupport, clearSilenceTimer]);

  const resetTranscript = useCallback(() => {
    clearSilenceTimer();
    setTranscript('');
    currentTranscriptRef.current = '';
  }, [clearSilenceTimer]);

  return {
    isListening,
    isPaused,
    transcript,
    error,
    hasSupport,
    startListening,
    stopListening,
    resetTranscript,
    setTranscript
  };
}
